import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";

const LicenseLedger = () => {
  const [reportList, setReportList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [consigneeId, setConsigneeId] = useState("");

  // ------------------------------
  // Alert Helper
  // ------------------------------
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({
      icon,
      title,
      text: text || "Something went wrong.",
      timer,
      showConfirmButton: false,
    });
  }, []);

  // ------------------------------
  // Fetch Data
  // ------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (consigneeId) payload.consignee_id = consigneeId;

      const response = await post("/reports/party_wise_license_ledger/list", payload);
      if (response.status === 200) {
        setReportList(response.data || []);
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [showAlert, consigneeId]);

  const fetchConsigneeData = useCallback(async () => {
    try {
      const response = await post("/masters/consignees/list");
      if (response.status === 200) {
        setConsigneeOptions([
          { value: "", label: "Select Consignee" },
          ...response.data.map(item => ({
            value: item.consignees_id,
            label: item.consignees_name,
          }))
        ]);
      }
    } catch (err) {
      console.log(err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchConsigneeData();
  }, [fetchData, fetchConsigneeData]);

  // ------------------------------
  // Table Columns
  // ------------------------------
  const columns = useMemo(
    () => [
      { header: "S.No", accessorKey: "si_no", enableColumnFilter: false, enableSorting: false },
      { header: "Job No", accessorKey: "job_no" },
      { header: "Consignee", accessorKey: "consignee" },
      { header: "Shipper", accessorKey: "shipper" },
      { header: "Desc", accessorKey: "description" },
      { header: "BL No", accessorKey: "bl_number" },
      { header: "TC", accessorKey: "no_of_containers", isTotal: true },
      { header: "20'", accessorKey: "count_20ft" },
      { header: "40'", accessorKey: "count_40ft" },
       { header: "LCL", accessorKey: "count_lcl" },
      { header: "Weight", accessorKey: "weight" , isTotal: true},
      { header: "BE No", accessorKey: "be_number" },
      { header: "BE DATE", accessorKey: "be_date" },
      { header: "License No", accessorKey: "license_no" },
      { header: "License Date", accessorKey: "license_date" },
      {
        header: "License Amount", accessorKey: "license_amount",
        cell: ({ row }) =>
          row.license_amount
            ? `₹ ${Number(row.license_amount).toLocaleString("en-IN")}`
            : "—"
      },
      {
        header: "License Available Amount", accessorKey: "license_available_amount",
        cell: ({ row }) =>
          row.license_available_amount
            ? `₹ ${Number(row.license_available_amount).toLocaleString("en-IN")}`
            : "—"
      },
      { header: "Bond No", accessorKey: "bond_no" },
      { header: "Bond Date", accessorKey: "bond_date" },
      {
        header: "Bond Amount", accessorKey: "bond_amount",
        cell: ({ row }) =>
          row.bond_amount
            ? `₹ ${Number(row.bond_amount).toLocaleString("en-IN")}`
            : "—"
      },
      {
        header: "Balance Rs", accessorKey: "bond_avail_value",
        cell: ({ row }) =>
          row.bond_avail_value
            ? `₹ ${Number(row.bond_avail_value).toLocaleString("en-IN")}`
            : "—"
      },
      { header: "Bank Guarantee No", accessorKey: "bank_guarantee_no" },
      { header: "Bank Guarantee Date", accessorKey: "bank_guarantee_date" },
      {
        header: "Bank Guarantee Amount", accessorKey: "bank_guarantee_amount",
        cell: ({ row }) =>
          row.bank_guarantee_amount
            ? `₹ ${Number(row.bank_guarantee_amount).toLocaleString("en-IN")}`
            : "—"
      },
    ],
    []
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Party Wise - License Ledger</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Consignee</label>
              <select
                className="form-select"
                value={consigneeId}
                onChange={(e) => setConsigneeId(e.target.value)}
              >
                {consigneeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" onClick={fetchData} disabled={loading}>
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Party Wise - License Ledger</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Party Wise License Ledger"
            columns={columns}
            data={reportList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>
    </>
  );
};

export default LicenseLedger;
